import React, { useState, useCallback } from "react";
import styles from "./ImageToJPG.module.css";

const ImageToJPG = () => {
  const [image, setImage] = useState(null);
  const [convertedImage, setConvertedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [quality, setQuality] = useState(90);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length) {
      processImage(files[0]);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImage(file);
    }
  };

  const processImage = (file) => {
    setError(null);

    if (!file.type.match("image.*")) {
      setError("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setImage({
          file,
          url: event.target.result,
          name: file.name,
          type: file.type,
          size: file.size,
          dimensions: { width: img.width, height: img.height },
        });
        setConvertedImage(null);
      };
      img.onerror = () => {
        setError("Failed to load image");
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      setError("Failed to read file");
    };
    reader.readAsDataURL(file);
  };

  const convertToJPG = () => {
    if (!image) {
      setError("No image to convert");
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // Fill with white background for transparent images
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        const jpgQuality = quality / 100;
        const jpgUrl = canvas.toDataURL("image/jpeg", jpgQuality);

        // Calculate file size
        const base64String = jpgUrl.split(",")[1];
        const fileSize = base64String
          ? Math.round((base64String.length * 3) / 4)
          : 0;

        setConvertedImage({
          url: jpgUrl,
          name: `${image.name.split(".")[0]}.jpg`,
          size: fileSize,
          dimensions: { width: img.width, height: img.height },
          quality: quality,
        });
      };
      img.onerror = () => {
        setError("Failed to process image");
      };
      img.src = image.url;
    } catch (err) {
      setError("Conversion failed: " + err.message);
    }
  };

  const downloadImage = () => {
    if (!convertedImage) {
      setError("No converted image to download");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = convertedImage.url;
      link.download = convertedImage.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError("Download failed: " + err.message);
    }
  };

  const resetAll = () => {
    setImage(null);
    setConvertedImage(null);
    setError(null);
    setQuality(90);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 bytes";
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Image to JPG Converter</h1>

      {/* Step Guide */}
      {!image && !convertedImage && (
        <div className={styles.stepsGuide}>
          <h2>How to Convert Images to JPG</h2>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <h4>Upload Image</h4>
                <p>Drag & drop or click to select your image file</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <h4>Convert to JPG</h4>
                <p>Adjust quality and convert your image to JPG format</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <h4>Download Result</h4>
                <p>Save your perfectly converted JPG image</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      {!image && !convertedImage && (
        <div className={styles.uploadSection}>
          <div
            className={`${styles.dropArea} ${
              isDragging ? styles.dragOver : ""
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById("fileInput").click()}
          >
            <div className={styles.dropContent}>
              <div className={styles.uploadIcon}>🖼️</div>
              <h3 className={styles.dropTitle}>Drop your image here</h3>
              <p className={styles.dropText}>or</p>
              <input
                type="file"
                id="fileInput"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.fileInput}
              />
              <button className={styles.browseButton}>Browse Files</button>
              <p className={styles.supportedFormats}>
                Supports: PNG, WEBP, GIF, BMP, and more
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Content Section */}
      {!image && !convertedImage && (
        <div className={styles.contentSection}>
          <div className={styles.contentGrid}>
            {/* Why JPG Section */}
            <div className={styles.contentBlock}>
              <h2>The International Language of Digital Photography</h2>
              <p>
                JPEG (Joint Photographic Experts Group), which was first
                standardized in 1992, continues to be the foundational format
                for digital images. Although modern options exist today, JPG is
                by far the leading and most widely-used digital photography
                format, and over 90% of images taken and shared as digital
                photos are saved as JPG images.
              </p>

              <div className={styles.benefitsGrid}>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🌍</div>
                  <h3>Universally Compatible</h3>
                  <p>
                    JPEG images will work on any device, browser or app made in
                    the last 30 years.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>💾</div>
                  <h3>Smaller File Sizes</h3>
                  <p>
                    Intelligent compression will reduce the file size by 50 to
                    90%, but not lose any visual fidelity.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>📸</div>
                  <h3>Photographic Fidelity</h3>
                  <p>
                    Designed specifically for pictures, JPG uses knowledge of
                    human vision and perception to eliminate elements not
                    noticed in possible perceptual detail.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>⚡</div>
                  <h3>Fast Processing</h3>
                  <p>
                    Hardware avoids any slow-down for decoding and encoding, the
                    former a display task and the latter a physical (camera)
                    capture task.
                  </p>
                </div>
              </div>
            </div>

            {/* JPG Compression Science */}
            <div className={styles.contentBlock}>
              <h2>The Science Behind JPG Compression</h2>
              <div className={styles.techSpecs}>
                <h3>Perceptual Compression System</h3>
                <p>
                  JPG uses a very advanced multi-staged systematic inverse
                  perceptual compression system based upon human vision
                  psychology to remove information that we are not, and will
                  not, perceive while still retaining the perceived visual
                  details of the data.
                </p>

                <div className={styles.compressionSteps}>
                  <div className={styles.compressionStep}>
                    <h4>Color Space Conversion</h4>
                    <p>
                      Convert RGB to YCbCr and completely separate brightness
                      from color.
                    </p>
                  </div>
                  <div className={styles.compressionStep}>
                    <h4>Chroma Subsampling</h4>
                    <p>
                      Remove color information while maintaining all brightness.
                    </p>
                  </div>
                  <div className={styles.compressionStep}>
                    <h4>Discrete Cosine Transform (DCT)</h4>
                    <p>Converts 8x8 pixel blocks to frequency data.</p>
                  </div>
                  <div className={styles.compressionStep}>
                    <h4>Quantization</h4>
                    <p>
                      Compress many of the information not notice while
                      maintaining this part of perceived visual detail.
                    </p>
                  </div>
                  <div className={styles.compressionStep}>
                    <h4>Entropy Encoding</h4>
                    <p>Removes statistical redundancy with Huffman coding.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quality Settings Guide */}
            <div className={styles.contentBlock}>
              <h2>Managing JPG Quality Settings</h2>
              <div className={styles.qualityGuide}>
                <div className={styles.qualityLevel}>
                  <div className={styles.qualityHeader}>
                    <h4>90 - 100% Quality</h4>
                    <span className={styles.qualityBadge}>Specialty</span>
                  </div>
                  <p>
                    <strong>Designed for:</strong> Professional photography,
                    print, and archiving.
                    <br />
                    <strong>Compression:</strong> Low (5-10%)
                    <br />
                    <strong>Visual Quality:</strong> Very little difference from
                    the original.
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <div className={styles.qualityHeader}>
                    <h4>75 - 89 Quality</h4>
                    <span className={styles.qualityBadge}>Optimal</span>
                  </div>
                  <p>
                    <strong>Designed for:</strong> Website and social media use.
                    <br />
                    <strong>Compression:</strong> 50-70%
                    <br />
                    <strong>Visual Quality:</strong> Very good for most
                    situations.
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <div className={styles.qualityHeader}>
                    <h4>50 - 74% Quality</h4>
                    <span className={styles.qualityBadge}>Efficient</span>
                  </div>
                  <p>
                    <strong>Designed for:</strong> Thumbnails and emailing.
                    <br />
                    <strong>Compression:</strong> 70 - 85%
                    <br />
                    <strong>Visual Quality:</strong> Acceptable, significant
                    artifacts.
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <div className={styles.qualityHeader}>
                    <h4>Below 50% Quality</h4>
                    <span className={styles.qualityBadge}>
                      Maximum Compression
                    </span>
                  </div>
                  <p>
                    <strong>Best when:</strong> Severe file size limits are in
                    place.
                    <br />
                    <strong>Compression:</strong> 85–95%
                    <br />
                    <strong>Visual Quality:</strong> Terrible, a lot of
                    artifacts.
                  </p>
                </div>
              </div>
            </div>

            {/* Professional Applications */}
            <div className={styles.contentBlock}>
              <h2>Professional JPG Uses</h2>
              <div className={styles.applicationsGrid}>
                <div className={styles.applicationItem}>
                  <h4>📷 Professional Photography</h4>
                  <p>As used in delivery to clients and portfolios.</p>
                </div>
                <div className={styles.applicationItem}>
                  <h4>🛒 E-commerce Platforms</h4>
                  <p>
                    Optimized product photography for e-commerce and online
                    stores.
                  </p>
                </div>
                <div className={styles.applicationItem}>
                  <h4>📱 Social Media Marketing</h4>
                  <p>Usable on all social media platforms.</p>
                </div>
                <div className={styles.applicationItem}>
                  <h4>📧 Email Marketing</h4>
                  <p>
                    Same as social media marketing, usable as an image for email
                    marketing.
                  </p>
                </div>
                <div className={styles.applicationItem}>
                  <h4>🌐 Web-Based Content</h4>
                  <p>Fast loading hero images or blog images.</p>
                </div>
                <div className={styles.applicationItem}>
                  <h4>🖨️ Digital Printing</h4>
                  <p>
                    Specifically intended for photo albums and photo prints.
                  </p>
                </div>
              </div>
            </div>

            {/* Advanced Optimization */}
            <div className={styles.contentBlock}>
              <h2>Advanced JPG Optimization Techniques</h2>
              <div className={styles.optimizationTips}>
                <div className={styles.optimizationTip}>
                  <h4>Progressive JPG Encoding</h4>
                  <p>
                    Images are displayed in multiple passes for the shortest
                    display time possible.
                  </p>
                </div>
                <div className={styles.optimizationTip}>
                  <h4>Optimal Size</h4>
                  <p>
                    Size your image before compression to help reduce bandwidth
                    use.
                  </p>
                </div>
                <div className={styles.optimizationTip}>
                  <h4>🎨 Color Profile Management</h4>
                  <p>
                    Always use sRGB color profile for web, will ensure correct
                    color management and color presentation.
                  </p>
                </div>
                <div className={styles.optimizationTip}>
                  <h4>⚡ Hardware Acceleration</h4>
                  <p>
                    JPG decoding is often much faster than newer file types.
                  </p>
                </div>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className={styles.contentBlock}>
              <h2>Technical Details of JPG</h2>
              <div className={styles.specsGrid}>
                <div className={styles.specItem}>
                  <h4>Maximum Dimensions</h4>
                  <p>65,535 × 65,535 pixels</p>
                </div>
                <div className={styles.specItem}>
                  <h4>Color Depth</h4>
                  <p>24-bit (16.7 million colors)</p>
                </div>
                <div className={styles.specItem}>
                  <h4>Compression Ratio</h4>
                  <p>10:1 to 100:1 (adjustable)</p>
                </div>
                <div className={styles.specItem}>
                  <h4>Color Spaces</h4>
                  <p>YCbCr, RGB, Grayscale</p>
                </div>
                <div className={styles.specItem}>
                  <h4>Metadata Support</h4>
                  <p>EXIF, IPTC, XMP, ICC profiles</p>
                </div>
                <div className={styles.specItem}>
                  <h4>Standardization</h4>
                  <p>ISO/IEC 10918-1</p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className={styles.contentBlock}>
              <h2>Frequently Asked Questions (FAQ)</h2>
              <div className={styles.faqGrid}>
                <div className={styles.faqItem}>
                  <h4>What's the difference between JPEG and JPG?</h4>
                  <p>
                    There isn't a difference. JPG is a file extension containing
                    three characters and JPEG is the name itself. Both refer to
                    the same format.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Why does JPG compression create artifacts?</h4>
                  <p>
                    JPG compression creates artifacts because it tries to
                    compress high-frequency visual data it believed was less
                    important. This can produce blocky, square patterns, or
                    color banding.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Does JPG support transparency?</h4>
                  <p>
                    No. JPG does not support transparency, instead use PNG or
                    WebP.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Is JPG appropriate for text and graphics?</h4>
                  <p>
                    No. JPG compression will likely create blurry text or
                    blurred sharp edges. PNG is best for these types of
                    graphics.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>What is the best quality setting for the Web?</h4>
                  <p>
                    75% to 85% setting will give you the best balance of file
                    size and visual quality.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Can I convert a JPG back to a lossless format?</h4>
                  <p>
                    Once compressed it cannot be recovered as the data has been
                    lost. Always keep your original images to edit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* Editor Section */}
      {(image || convertedImage) && (
        <div className={styles.editorSection}>
          <div className={styles.mainContent}>
            {/* Editor Side */}
            <div className={styles.editorSide}>
              <div className={styles.sectionHeader}>
                <h3>Original Image</h3>
                <button onClick={resetAll} className={styles.changeImageButton}>
                  Change Image
                </button>
              </div>

              {image && (
                <>
                  <div className={styles.cropSection}>
                    <img
                      src={image.url}
                      alt="Original"
                      className={styles.cropImage}
                    />
                  </div>

                  <div className={styles.controls}>
                    <div className={styles.qualityControl}>
                      <label htmlFor="qualitySlider">
                        Image Quality: {quality}%
                      </label>
                      <input
                        type="range"
                        id="qualitySlider"
                        min="10"
                        max="100"
                        value={quality}
                        onChange={(e) => setQuality(parseInt(e.target.value))}
                        className={styles.qualitySlider}
                      />
                    </div>

                    <div className={styles.buttonGroup}>
                      <button
                        onClick={convertToJPG}
                        className={styles.processButton}
                      >
                        Convert to JPG
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Preview Side */}
            {convertedImage && (
              <div className={styles.previewSide}>
                <div className={styles.previewSection}>
                  <h3>Converted JPG Image</h3>
                  <div className={styles.previewContent}>
                    <div className={styles.previewImageContainer}>
                      <img
                        src={convertedImage.url}
                        alt="Converted JPG"
                        className={styles.previewImage}
                      />
                    </div>

                    <div className={styles.imageInfo}>
                      <h4>File Information</h4>
                      <div className={styles.detailItem}>
                        <span>Format:</span>
                        <strong>JPG</strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Quality:</span>
                        <strong>{convertedImage.quality}%</strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Dimensions:</span>
                        <strong>
                          {convertedImage.dimensions.width} ×{" "}
                          {convertedImage.dimensions.height}
                        </strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>File Size:</span>
                        <strong>{formatFileSize(convertedImage.size)}</strong>
                      </div>
                    </div>

                    <button
                      onClick={downloadImage}
                      className={styles.downloadButton}
                    >
                      Download JPG
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageToJPG;
